import {z} from 'zod';
import {extensionKeySchema, secondsOrMsSchema} from '../shared';
import {standardPlaybackControlSchema} from './control';

const negateSchema = z.literal(1);

const conditionValueSchema = z.unknown();

export const conditionTupleSchema = z.union([
  z.tuple([
    /** Extension key for this condition */
    extensionKeySchema,
    /** Value to compare against */
    conditionValueSchema,
    /** Optional negate flag */
    negateSchema,
  ]),
  z.tuple([extensionKeySchema, conditionValueSchema]),
]);
export type ConditionTuple<T = unknown> =
  | [z.infer<typeof extensionKeySchema>, T, z.infer<typeof negateSchema>]
  | [z.infer<typeof extensionKeySchema>, T];
export type Condition<T = unknown> = ConditionTuple<T>;

export const conditionObjectSchema = z.object({
  /** Extension key for this condition */
  extensionKey: extensionKeySchema,
  /** Value to compare against */
  value: conditionValueSchema,
  /** Optional negate flag */
  negate: z.boolean().optional().default(false),
});
export type ConditionObject<T = unknown> = {
  extensionKey: z.infer<typeof extensionKeySchema>;
  value: T;
  negate: boolean;
};

export const conditionalOutcomeSchema = z.object({
  /** Control action to perform on the timeline */
  control: standardPlaybackControlSchema,
  /**
   * Target timeline ID for this outcome
   * TIMELINE TARGETING SCOPE:
   * Currently scoped to timelines within the same interaction for simplicity and performance.
   * When targetTimelineId is undefined, applies to all timelines in the current interaction.
   *
   * FUTURE EXPANSION CAPABILITY:
   * This field is designed to support cross-interaction targeting in the future by allowing:
   * - InteractionId.TimelineId format (e.g., "other-interaction.timeline-1")
   * - Separate targetInteractionId field
   *
   * Cross-interaction targeting would require:
   * 1. Cross-interaction dependency tracking for proper lifecycle management
   * 2. Enhanced cleanup logic when interactions are removed to prevent dangling references
   * 3. UI updates to visualize cross-interaction relationships and dependencies
   * 4. Performance considerations for large interaction sets with complex dependencies
   * 5. Validation to prevent circular dependencies between interactions
   */
  targetTimelineId: z.string().optional(),
  /** Playback speed multiplier for this outcome */
  speed: z.number().positive().optional(),
  /** Position in seconds to jump to when triggered */
  jump: secondsOrMsSchema.optional(),
  /** Delay in seconds before the trigger activates the interaction */
  delay: secondsOrMsSchema.optional(),
});
export type ConditionalOutcome = z.infer<typeof conditionalOutcomeSchema>;

export const triggerConditionalLogicTupleSchema = z.object({
  /** Array of conditions that must be evaluated */
  conditions: z.array(conditionTupleSchema),
  /** Outcome to execute when conditions evaluate to true */
  ifTrue: conditionalOutcomeSchema,
  /** Optional outcome to execute when conditions evaluate to false */
  ifFalse: conditionalOutcomeSchema.optional(),
});
export type TriggerConditionalLogic = z.infer<
  typeof triggerConditionalLogicTupleSchema
>;

export const triggerConditionalLogicObjectSchema = z.object({
  /** Array of conditions that must be evaluated */
  conditions: z.array(conditionObjectSchema),
  /** Outcome to execute when conditions evaluate to true */
  ifTrue: conditionalOutcomeSchema,
  /** Optional outcome to execute when conditions evaluate to false */
  ifFalse: conditionalOutcomeSchema.optional(),
});
export type TriggerConditionalLogicObject = z.infer<
  typeof triggerConditionalLogicObjectSchema
>;

export const coreOperatorsSchema = z.enum(['wf:and', 'wf:or']);
export const enum CoreOperators {
  AND = 'wf:and',
  OR = 'wf:or',
}

export const conditionTransforms = {
  tupleToObject: <T = unknown>(
    tuple: ConditionTuple<T>
  ): ConditionObject<T> => {
    const [extensionKey, value, negate] = tuple;
    return {
      extensionKey,
      value,
      negate: negate === 1,
    };
  },

  objectToTuple: <T = unknown>(obj: ConditionObject<T>): ConditionTuple<T> => {
    if (obj.negate) {
      return [obj.extensionKey, obj.value, 1];
    }
    return [obj.extensionKey, obj.value];
  },
};
